import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  LinearProgress,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Description as ReportIcon,
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchDoctorDashboard } from '../../store/slices/dashboardSlice';
import { fetchTodaysPendingAppointments, fetchTodaysAcceptedAppointments } from '../../store/slices/appointmentSlice';

/**
 * Doctor Dashboard Page
 * Premium dashboard for Government Doctors with workflow actions.
 *
 * @author Civilhealth Development Team
 */
const DoctorDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { doctorDashboard, loading } = useSelector((state: RootState) => state.dashboard);
  const { appointments: pendingAppointments, loading: pendingLoading } = useSelector(
    (state: RootState) => state.appointment
  );

  const [workflowStats, setWorkflowStats] = useState({
    pending: 0,
    accepted: 0,
    inProgress: 0,
    completed: 0,
  });

  // Redirect if not authenticated or not a doctor
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (user && user.role !== 'GOVERNMENT_DOCTOR') {
      console.log('Unauthorized access attempt by role:', user.role);
      navigate('/', { replace: true });
      return;
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'GOVERNMENT_DOCTOR') {
      dispatch(fetchDoctorDashboard());
      dispatch(fetchTodaysPendingAppointments());
    }
  }, [dispatch, isAuthenticated, user]);

  useEffect(() => {
    if (pendingAppointments && pendingAppointments.length > 0) {
      setWorkflowStats((prev) => ({
        ...prev,
        pending: pendingAppointments.filter((a: any) => a.status === 'PENDING').length,
      }));
    }
  }, [pendingAppointments]);

  if (loading || !doctorDashboard) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  const { stats, todayAppointments, upcomingAppointments, weeklySchedule } = doctorDashboard;

  // Workflow Steps - NO BACKGROUND, just icon color
  const workflowSteps = [
    {
      title: 'Pending',
      description: 'Awaiting your acceptance',
      icon: <ScheduleIcon />,
      count: workflowStats.pending || stats.pendingAppointments || 0,
      color: '#B45309',
      action: 'Accept',
      path: '/doctor/workflow',
    },
    {
      title: 'Accepted',
      description: 'Ready for visit',
      icon: <CheckIcon />,
      count: workflowStats.accepted || 0,
      color: '#1D4ED8',
      action: 'Start Visit',
      path: '/doctor/workflow',
    },
    {
      title: 'In Progress',
      description: 'Currently examining',
      icon: <HospitalIcon />,
      count: workflowStats.inProgress || 0,
      color: '#1D4ED8',
      action: 'Continue',
      path: '/doctor/workflow',
    },
    {
      title: 'Completed',
      description: 'Cases closed today',
      icon: <ReportIcon />,
      count: stats.completedAppointments || 0,
      color: '#15803D',
      action: 'View',
      path: '/appointments',
    },
  ];

  // Stats cards data - NO BACKGROUND, just icon color
  const statsCards = [
    { title: "Today's Appointments", value: stats.todayAppointments || 0, icon: <CalendarIcon />, color: '#1D4ED8' },
    { title: 'Pending Acceptance', value: stats.pendingAppointments || 0, icon: <ScheduleIcon />, color: '#B45309' },
    { title: 'Completed Today', value: stats.completedAppointments || 0, icon: <PeopleIcon />, color: '#15803D' },
    { title: 'Visit Reports', value: stats.totalVisitReports || 0, icon: <ReportIcon />, color: '#DC2626' },
  ];

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'doctor_assigned':
      case 'doctor_on_the_way':
      case 'in_progress':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pending';
      case 'CONFIRMED':
        return 'Confirmed';
      case 'DOCTOR_ASSIGNED':
        return 'Accepted';
      case 'DOCTOR_ON_THE_WAY':
        return 'On The Way';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'COMPLETED':
        return 'Completed';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return status;
    }
  };

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#202124' }}>
            Doctor Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your appointments and patient visits
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AssignmentIcon />}
          onClick={() => navigate('/doctor/workflow')}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
          }}
        >
          Start Workflow
        </Button>
      </Box>

      {/* Workflow Quick Actions - CLEAN BACKGROUND */}
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2, backgroundColor: '#FFFFFF' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Today's Workflow
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {workflowSteps.map((step, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                sx={{
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '1px solid transparent',
                  '&:hover': {
                    borderColor: step.color,
                    boxShadow: `0 4px 12px ${step.color}20`,
                  },
                }}
                onClick={() => navigate(step.path)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  {/* NO BACKGROUND - Icon only */}
                  <Avatar sx={{ bgcolor: 'transparent', color: step.color, width: 48, height: 48, mx: 'auto', mb: 1 }}>
                    {step.icon}
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: step.color }}>
                    {step.count}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {step.description}
                  </Typography>
                  <Chip label={step.action} size="small" variant="outlined" sx={{ color: step.color, borderColor: step.color }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {card.value.toLocaleString()}
                    </Typography>
                  </Box>
                  {/* NO BACKGROUND - Icon only with color */}
                  <Avatar sx={{ backgroundColor: 'transparent', color: card.color, width: 56, height: 56 }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions Alert */}
      {stats.pendingAppointments > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/doctor/workflow')}
              endIcon={<ArrowIcon />}
            >
              Accept Now
            </Button>
          }
        >
          You have {stats.pendingAppointments} pending appointment{stats.pendingAppointments > 1 ? 's' : ''} waiting for your acceptance.
        </Alert>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Today's Appointments */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
            <CardHeader
              title="Today's Appointments"
              subheader={`${new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}
              action={
                <Button size="small" endIcon={<ArrowIcon />} onClick={() => navigate('/doctor/workflow')}>
                  Go to Workflow
                </Button>
              }
            />
            <CardContent>
              {todayAppointments && todayAppointments.length > 0 ? (
                <List>
                  {todayAppointments.slice(0, 5).map((appointment: any, index: number) => (
                    <React.Fragment key={appointment.id || index}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                            <PeopleIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {appointment.patientName || appointment.officerName}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {appointment.appointmentTime} - {appointment.endTime}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Type: {appointment.appointmentType?.replace('_', ' ')}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                            <Chip
                              label={getStatusLabel(appointment.status)}
                              size="small"
                              color={getStatusColor(appointment.status)}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/appointments/${appointment.id}`)}
                            >
                              View
                            </Button>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < Math.min(todayAppointments.length - 1, 4) && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CalendarIcon sx={{ fontSize: 64, color: '#dadce0', mb: 2 }} />
                  <Typography color="text.secondary">
                    No appointments scheduled for today
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly Schedule */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12)', height: '100%' }}>
            <CardHeader
              title="This Week"
              subheader="Your availability"
              action={
                <Button size="small" onClick={() => navigate('/settings')}>
                  Edit
                </Button>
              }
            />
            <CardContent>
              {weeklySchedule && weeklySchedule.length > 0 ? (
                <Box>
                  {weeklySchedule.map((day: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1.5,
                        borderBottom: index < weeklySchedule.length - 1 ? '1px solid #eee' : 'none',
                      }}
                    >
                      {/* NO BACKGROUND - Icon only */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: 'transparent',
                            color: day.isAvailable ? '#15803D' : '#DC2626',
                          }}
                        >
                          {day.dayOfWeek?.charAt(0) || 'S'}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {day.dayOfWeek}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {day.isAvailable
                              ? `${day.startTime} - ${day.endTime}`
                              : day.leaveType || 'Not Available'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        {day.isAvailable ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e8e3e' }}>
                              {day.availableSlots} / {day.totalSlots}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              slots available
                            </Typography>
                          </Box>
                        ) : (
                          <Chip label="Off" size="small" color="error" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No schedule configured
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DoctorDashboardPage;